import { useEffect, useState } from "react";
import { motion, useScroll, useSpring, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { Button } from "./primitives";

/** The live product. */
const APP_URL = "https://app.playerdna.com";

/**
 * Thin scroll-progress bar pinned to the top of the viewport. Mounts only after
 * the first paint so its scroll measurement never competes with rendering the
 * page (which made content feel like it waited for the bar to initialize).
 */
export function ScrollProgress() {
  const [ready, setReady] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  if (!ready) return null;
  return (
    <motion.div
      aria-hidden
      style={{ scaleX }}
      className="fixed inset-x-0 top-0 z-50 h-0.5 origin-left bg-gradient-to-r from-brand via-accent to-gold"
    />
  );
}

const links = [
  { href: "#framework", label: "Framework" },
  { href: "#dashboard", label: "Dashboard" },
  { href: "#velocity", label: "Velocity" },
  { href: "#share", label: "Sharing" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();

  // Full-width glass bar: transparent over the hero, frosted once scrolled.
  // Auto-hide on scroll down, reveal on scroll up.
  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(y > 8);
    if (open) {
      setHidden(false);
      return;
    }
    setHidden(y > prev && y > 90);
  });

  return (
    <motion.header
      initial={false}
      animate={{ y: hidden ? "-100%" : "0%" }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-40"
    >
      <div
        className={`w-full border-b transition-colors duration-300 ${
          scrolled || open ? "border-line/60 glass" : "border-transparent"
        }`}
      >
        <div className="mx-auto flex w-[min(80rem,calc(100%-1.5rem))] items-center justify-between py-3">
          <a href="#top" className="flex items-center">
            <BrandLogo />
          </a>

          <nav className="hidden items-center gap-7 md:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm text-ink-soft transition-colors hover:text-ink"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-5 md:flex">
            <a href={APP_URL} className="text-sm font-semibold text-ink transition-colors hover:text-brand">
              Log in
            </a>
            <Button href="#early-access" className="px-5 py-2 text-xs">
              Join Early Access
            </Button>
          </div>

          <button
            className="grid h-9 w-9 place-items-center rounded-full border border-line text-ink md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>

        {open && (
          <div className="mx-auto w-[min(80rem,calc(100%-1.5rem))] pb-3 md:hidden">
            <nav className="flex flex-col rounded-2xl border border-line/70 bg-elevated/40 p-2">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm text-ink-soft hover:bg-elevated"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={APP_URL}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-semibold text-ink hover:bg-elevated"
              >
                Log in
              </a>
              <Button href="#early-access" className="mt-2">
                Join Early Access
              </Button>
            </nav>
          </div>
        )}
      </div>
    </motion.header>
  );
}

/**
 * Section photo: object-cover with a hairline inset ring. On the light theme photos stay
 * crisp; pass `overlay` only where the image must melt into the page (it is confined to
 * the bottom third). Lazy by default; pass `priority` for above-the-fold images.
 */
export function SectionImage({
  src,
  alt,
  className = "",
  priority = false,
  overlay = "",
  // Bias the crop toward the subject (athletes sit a little above center), so
  // heads/upper bodies aren't cut off in wide bands.
  position = "50% 32%",
}: {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  overlay?: string;
  position?: string;
}) {
  return (
    <figure className={`relative overflow-hidden rounded-2xl border border-line/70 ${className}`}>
      <img
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        className="h-full w-full object-cover"
        style={{ objectPosition: position }}
      />
      {overlay && (
        <div className={`pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t ${overlay} to-transparent`} />
      )}
      <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-line/60" />
    </figure>
  );
}

/**
 * Labelled art slot — a designed placeholder for cinematic/editorial imagery
 * that hasn't been generated yet. Reads as an intentional art frame (not a
 * broken box) and surfaces the exact prompt name so art can be dropped in per
 * docs/image-prompts.md. `caption` describes the intended shot.
 */
export function ImageSlot({
  name,
  caption,
  className = "",
}: {
  name: string;
  caption?: string;
  className?: string;
}) {
  return (
    <div
      role="img"
      aria-label={`Placeholder for ${name}`}
      className={`group relative grid place-items-center overflow-hidden rounded-2xl border border-line/70 bg-gradient-to-b from-elevated/60 to-surface/60 ${className}`}
    >
      <div className="bg-field-grid absolute inset-0 opacity-40" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-background/30 to-transparent" />
      <div className="relative flex flex-col items-center gap-3 px-4 text-center">
        <span className="grid h-11 w-11 place-items-center rounded-xl border border-line bg-background/70 text-ink-muted">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.7}>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="9" cy="11" r="2" />
            <path d="m4 18 5-4 4 3 3-2 4 3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="rounded-full border border-line bg-background/80 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-soft">
          {name}
        </span>
        {caption && <span className="max-w-[15rem] text-[11px] leading-snug text-ink-muted">{caption}</span>}
      </div>
    </div>
  );
}
